import React from 'react'
import classNames from 'classnames'
import { useTranslate } from '@/utils/translate/translate-client'

/** Large Chromecast icon for the status overlay (64x64). */
const ChromecastLargeIcon: React.FC = () => (
  <svg width={64} height={64} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#fff"
      d="M27.7 5.4H6.4c-1.2 0-2.1 1-2.1 2.1v2.1h2.1V7.5h21.3v12.8h-8.5v2.1h8.5c1.2 0 2.1-1 2.1-2.1V7.5c0-1.2-.9-2.1-2.1-2.1z"
    />
    <path
      fill="#fff"
      d="M2.2 12.8v2.1c6.5 0 11.7 5.2 11.7 11.7H16C16 19 9.8 12.8 2.2 12.8zM2.2 18.1v2.1c3.5 0 6.4 2.9 6.4 6.4h2.1c0-4.6-3.8-8.5-8.5-8.5zM2.2 23.4v3.2h3.2c0-1.8-1.4-3.2-3.2-3.2z"
    />
  </svg>
)

type CastState = 'unavailable' | 'available' | 'connecting' | 'connected'

interface HlsCastStatusOverlayProps {
  castState: CastState
  deviceName: string | null
}

/**
 * Full-screen overlay displayed when a Google Cast session is active.
 * Shows a large Chromecast icon with status text:
 * - Connecting: "Connecting to {device}..."
 * - Connected: "Playing on {device}"
 *
 * Matches the Bitmovin CastStatusOverlay styling.
 */
export const HlsCastStatusOverlay: React.FC<HlsCastStatusOverlayProps> = ({ castState, deviceName }) => {
  const { t } = useTranslate('common')

  const isVisible = castState === 'connecting' || castState === 'connected'

  const statusText =
    castState === 'connecting'
      ? t('castConnecting', 'Connecting to {{device}}...', { device: deviceName ?? t('castDevice', 'device') })
      : t('castPlaying', 'Playing on {{device}}', { device: deviceName ?? t('castDevice', 'device') })

  return (
    <div
      className={classNames(
        'absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/80 transition-opacity duration-300',
        isVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
    >
      <ChromecastLargeIcon />
      <p className="photon-title-sm mt-4 text-white">{statusText}</p>
    </div>
  )
}
